import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatList, MatListItem, MatListOption, MatSelectionList } from '@angular/material/list';
import { MatRadioButton, MatRadioChange, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { Moment } from 'moment';

@Component({
  selector: 'vectronic-header-field',
  templateUrl: './header-field.component.html',
  styleUrls: ['./header-field.component.scss'],
  imports: [
    MatList,
    MatListItem,
    MatSelectionList,
    ReactiveFormsModule,
    MatListOption,
    MatFormField,
    MatLabel,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatRadioGroup,
    MatRadioButton,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderFieldComponent {
  label = input<string>();
  type = input<
    'radio' | 'autocomplete' | 'number' | 'date' | 'chips' | 'list' | 'multi' | 'select'
  >();
  max = input<number>();
  min = input<number>();
  minDate = input<string>();
  maxDate = input<string>();
  options = input<
    {
      value: string | number;
      label: string;
    }[]
  >();
  isClosed = input<boolean>();
  value = input<any>();
  control = input<FormControl>();

  filteredOptions = computed(() =>
    this.options()?.filter((item) => !this.chipsOptions().includes(item.value as string)),
  );
  chipsOptions = signal<string[]>([]);
  valueChange = output<string | number | Date | string[]>();
  isClosedChange = output<boolean>();

  inputFormControl = new FormControl('');
  startDatePicker = viewChild<MatDatepicker<Moment>>('startDatePicker');

  constructor() {
    toObservable(this.value)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const selectedItem = this.options()?.find((item) => item.value === this.control().value);
        this.inputFormControl.setValue(selectedItem?.label || this.control().value);
      });
  }

  optionSelected({ option }: MatAutocompleteSelectedEvent) {
    this.control().setValue(option.value);
    this.valueChange.emit(option.value);
  }

  chipOptionSelected({ option }: MatAutocompleteSelectedEvent) {
    this.chipsOptions.set([...this.chipsOptions(), option.value]);
    this.control().setValue(this.chipsOptions());
    this.valueChange.emit(option.value);
  }

  setFocusDatePicker() {
    this.startDatePicker()?.open();
  }

  displayFn(value: any) {
    const selectedItem = this.options()?.find((item) => item.value == value);
    return selectedItem?.label || value;
  }

  onNumberChange() {
    this.valueChange.emit(this.control().value);
  }

  onDateChanged({ value }: MatDatepickerInputEvent<Date>) {
    this.valueChange.emit(value);
  }

  radioOptionSelected({ value }: MatRadioChange) {
    this.valueChange.emit(value);
  }
  removeChip(event: string) {
    this.chipsOptions.set(this.chipsOptions().filter((i) => i !== event));
    this.control().setValue(this.chipsOptions());
  }

  onSelectItem(value: string | number) {
    this.control().setValue(value);
    this.valueChange.emit(value);
    this.isClosedChange.emit(true);
  }

  onMultiChange() {
    this.valueChange.emit(this.control().value);
  }
}
